import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import { ChevronDown, RotateCw } from 'lucide-react';

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const InquiriesTab = () => {
  const { hasPermission } = useAuth();

  // Filters State
  const [inquiryFilters, setInquiryFilters] = useState({ vendorEmail: '', startDate: '', endDate: '', city: '', category_id: '' });
  const [vendorSearchText, setVendorSearchText] = useState('');
  const [citySearchInput, setCitySearchInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Pagination State
  const [inquiryPage, setInquiryPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_inquiries');
      return saved ? Math.max(0, parseInt(saved, 10)) : 0;
    } catch (e) {
      return 0;
    }
  });

  const setInquiryPage = useCallback((valOrFn) => {
    setInquiryPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_inquiries', String(next));
      } catch (e) {}
      return next;
    });
  }, []);

  const [inquiryPageSize, setInquiryPageSize] = useState(25);
  const [inquiryTotalPages, setInquiryTotalPages] = useState(0);
  const [inquiryTotalItems, setInquiryTotalItems] = useState(0);

  // Data States
  const [inquiries, setInquiries] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [inquiriesLoading, setInquiriesLoading] = useState(true);

  const fetchInquiries = useCallback(async () => {
    if (!hasPermission('MANAGE_MEMBERSHIPS')) return;
    if ((inquiryFilters.startDate && !inquiryFilters.endDate) || (!inquiryFilters.startDate && inquiryFilters.endDate)) {
      return;
    }
    setInquiriesLoading(true);
    try {
      const res = await vendorService.getAdminInquiries(inquiryFilters, inquiryPage, inquiryPageSize);
      if (res.success && res.data) {
        setInquiries(res.data.inquiries || []);
        setInquiryTotalPages(res.data.totalPages || 0);
        setInquiryTotalItems(res.data.totalItems || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setInquiriesLoading(false);
    }
  }, [hasPermission, inquiryFilters, inquiryPage, inquiryPageSize]);

  const fetchAllVendors = useCallback(async () => {
    if (!hasPermission('MANAGE_MEMBERSHIPS')) return;
    try {
      const res = await vendorService.getAdminAllVendors();
      if (res.success && res.data) {
        setAllVendors(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [hasPermission]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await vendorService.getCategories(true);
      if (res.success && res.data) {
        setCategories(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  useEffect(() => {
    fetchAllVendors();
    fetchCategories();
  }, [fetchAllVendors, fetchCategories]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 overflow-hidden text-left">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 select-none">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Customers' Inquiries</h2>
          <p className="text-xs text-gray-400 mt-1 font-medium">View and audit all direct customer inquiries submitted to vendor listings.</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-4 p-4 mb-6 border border-gray-100 bg-gray-50/50 rounded-xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 w-full">
          {/* Search Vendor input */}
          <div className="flex flex-col gap-1.5 relative">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider select-none">Search Vendor</label>
            <div className="relative">
              <input
                type="text"
                value={vendorSearchText}
                onChange={(e) => {
                  setVendorSearchText(e.target.value);
                  setShowSuggestions(true);
                  if (!e.target.value) {
                    setInquiryFilters(prev => ({ ...prev, vendorEmail: '' }));
                    setInquiryPage(0);
                  }
                }}
                onFocus={(e) => {
                  setShowSuggestions(true);
                  e.target.select();
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowSuggestions(false);
                    const val = vendorSearchText.trim();
                    const emailMatch = val.match(/\(([^)]+)\)$/);
                    const emailToFilter = emailMatch ? emailMatch[1] : val;
                    setInquiryFilters(prev => ({ ...prev, vendorEmail: emailToFilter }));
                  }, 250);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const val = vendorSearchText.trim();
                    const emailMatch = val.match(/\(([^)]+)\)$/);
                    const emailToFilter = emailMatch ? emailMatch[1] : val;
                    setInquiryFilters(prev => ({ ...prev, vendorEmail: emailToFilter }));
                    setShowSuggestions(false);
                    setInquiryPage(0);
                  }
                }}
                placeholder="Type Company Name or Email..."
                className="w-full bg-white border border-gray-200 rounded-lg py-1.5 px-3 text-xs font-semibold text-gray-700 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none"
              />
              {showSuggestions && (
                <div className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                  {(() => {
                    const term = vendorSearchText.toLowerCase();
                    const cleanTerm = term.includes(' (') ? term.split(' (')[0].trim() : term;
                    const suggestions = allVendors.filter(v => 
                      (v.company_name || '').toLowerCase().includes(cleanTerm) || 
                      (v.email || '').toLowerCase().includes(cleanTerm)
                    );
                    if (suggestions.length === 0) {
                      return <div className="p-3 text-xs text-gray-400 font-medium italic">No matching vendors</div>;
                    }
                    return suggestions.map(vendor => (
                      <div
                        key={vendor.email}
                        onMouseDown={() => {
                          const formatted = vendor.company_name ? `${vendor.company_name} (${vendor.email})` : vendor.email;
                          setInquiryFilters(prev => ({ ...prev, vendorEmail: vendor.email }));
                          setVendorSearchText(formatted);
                          setShowSuggestions(false);
                          setInquiryPage(0);
                        }}
                        className="p-2.5 hover:bg-[#2ab391]/10 text-xs font-semibold text-gray-700 cursor-pointer border-b border-gray-50 last:border-0 transition-colors"
                      >
                        <div className="font-bold text-gray-800">{vendor.company_name || 'N/A'}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{vendor.email}</div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Vendor City filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider select-none">Filter Vendor City</label>
            <input
              type="text"
              value={citySearchInput}
              onChange={(e) => setCitySearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setInquiryFilters(prev => ({ ...prev, city: citySearchInput }));
                  setInquiryPage(0);
                }
              }}
              placeholder="Enter city & press Enter..."
              className="w-full bg-white border border-gray-200 rounded-lg py-1.5 px-3 text-xs font-semibold text-gray-700 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none"
            />
          </div>

          {/* Category filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider select-none">Filter Category</label>
            <select
              value={inquiryFilters.category_id || ''}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : '';
                setInquiryFilters(prev => ({ ...prev, category_id: val }));
                setInquiryPage(0);
              }}
              className="w-full bg-white border border-gray-200 rounded-lg py-1.5 px-3 text-xs font-semibold text-gray-700 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none cursor-pointer"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Date Range picker */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider select-none">Date Range</label>
            <DateRangePicker
              startDate={inquiryFilters.startDate}
              endDate={inquiryFilters.endDate}
              onChange={(start, end) => {
                setInquiryFilters(prev => ({ ...prev, startDate: start, endDate: end }));
                setInquiryPage(0);
              }}
              placeholder="Select Date Range"
              className="w-full bg-white hover:bg-slate-50 border-gray-300 font-semibold"
            />
          </div>
        </div>

        {/* Clear filters button */}
        {(inquiryFilters.vendorEmail || inquiryFilters.startDate || inquiryFilters.endDate || inquiryFilters.city || inquiryFilters.category_id || vendorSearchText || citySearchInput) && (
          <div className="flex justify-end pt-2 border-t border-gray-100 select-none">
            <button
              onClick={() => {
                setInquiryFilters({ vendorEmail: '', startDate: '', endDate: '', city: '', category_id: '' });
                setVendorSearchText('');
                setCitySearchInput('');
                setInquiryPage(0);
              }}
              className="px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors cursor-pointer"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Inquiry Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
              <th className="p-4 pl-6">Vendor & Listing</th>
              <th className="p-4">Customer Contact</th>
              <th className="p-4">Vendor City</th>
              <th className="p-4">Category</th>
              <th className="p-4">Message</th>
              <th className="p-4">Submitted At</th>
              <th className="p-4 pr-6">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
            {inquiriesLoading ? (
              <tr>
                <td colSpan="7" className="p-12 text-center text-gray-400 font-semibold select-none">
                  <div className="flex flex-col justify-center items-center">
                    <div className="w-8 h-8 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs text-gray-500 font-semibold mt-2">Loading inquiries...</span>
                  </div>
                </td>
              </tr>
            ) : inquiries.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-12 text-center text-gray-400 font-semibold">
                  No inquiries found.
                </td>
              </tr>
            ) : (
              inquiries.map(inq => (
                <tr key={inq.id} className="hover:bg-gray-50 transition-all border-b border-gray-100 last:border-0">
                  <td className="p-4 pl-6 text-left">
                    <div className="font-semibold text-gray-800">{inq.company_name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{inq.vendor_email}</div>
                  </td>
                  <td className="p-4 text-left">
                    <div className="font-semibold text-gray-800">{inq.name}</div>
                    <div className="text-xs text-gray-500 font-mono mt-0.5">{inq.phone}</div>
                  </td>
                  <td className="p-4 font-semibold text-[#2ab391] text-left">
                    {inq.vendor_city || 'N/A'}
                  </td>
                  <td className="p-4 text-left">
                    <span className="px-2 py-0.5 text-xs font-bold bg-[#eaf7f4] text-[#2ab391] rounded border border-teal-100/50 whitespace-nowrap">
                      {inq.category_name || 'Uncategorized'}
                    </span>
                  </td>
                  <td className="p-4 max-w-[260px] text-left">
                    <p className="text-xs text-gray-600 leading-relaxed font-medium whitespace-pre-line truncate hover:whitespace-normal hover:overflow-visible hover:max-w-none transition-all" title={inq.message}>
                      {inq.message}
                    </p>
                  </td>
                  <td className="p-4 text-xs font-semibold text-gray-500 text-left">
                    {inq.created_at ? new Date(inq.created_at).toLocaleDateString('en-IN') + ' ' + new Date(inq.created_at).toLocaleTimeString('en-IN', {hour: '2-digit', minute:'2-digit'}) : 'N/A'}
                  </td>
                  <td className="p-4 pr-6 text-left">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                      inq.status === 'APPROVED' || inq.status === 'WON' ? 'bg-emerald-50 text-emerald-700' :
                      inq.status === 'REJECTED' || inq.status === 'LOST' ? 'bg-rose-50 text-rose-700' :
                      inq.status === 'HOT' ? 'bg-teal-50 text-teal-700' :
                      inq.status === 'WARM' ? 'bg-amber-50 text-amber-700' :
                      inq.status === 'COLD' ? 'bg-slate-100 text-slate-700' :
                      'bg-blue-50 text-blue-700'
                    }`}>
                      {inq.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-500 font-medium border-t border-gray-100 pt-5 mt-6 select-none bg-gray-50/40">
        <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
          {inquiryTotalItems === 0 ? (
            'No records available'
          ) : (
            `Showing ${inquiryPage * inquiryPageSize + 1} to ${Math.min((inquiryPage + 1) * inquiryPageSize, inquiryTotalItems)} of ${inquiryTotalItems} entries`
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
          <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => fetchInquiries()}
              disabled={inquiriesLoading}
              title="Reload current page"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-xl font-bold text-xs transition-all cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCw size={12} className={inquiriesLoading ? 'animate-spin' : ''} />
              Reload
            </button>
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Show</span>
            <div className="relative">
              <select
                value={inquiryPageSize}
                onChange={(e) => {
                  setInquiryPageSize(Number(e.target.value));
                  setInquiryPage(0);
                }}
                className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={12} />
              </span>
            </div>
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
          </div>

          {inquiryTotalPages > 0 && (
            <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setInquiryPage(p => Math.max(0, p - 1))}
                disabled={inquiryPage === 0}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Previous
              </button>

              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers(inquiryPage, inquiryTotalPages).map((p, idx) => {
                  if (p === 'ellipsis1' || p === 'ellipsis2') {
                    return <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setInquiryPage(p)}
                      className={`w-8 h-8 rounded-xl font-bold text-xs flex items-center justify-center transition-all cursor-pointer ${
                        inquiryPage === p
                          ? 'bg-[#2ab391] text-white shadow-md shadow-[#2ab391]/10'
                          : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p + 1}
                    </button>
                  );
                })}
              </div>

              <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm min-w-[70px] text-center">
                {inquiryPage + 1} / {inquiryTotalPages}
              </span>

              <button
                type="button"
                onClick={() => setInquiryPage(p => Math.min(inquiryTotalPages - 1, p + 1))}
                disabled={inquiryPage >= inquiryTotalPages - 1}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InquiriesTab;
